import React, { useMemo } from 'react';

import { Badge, Box, Flex, Text } from '@chakra-ui/react';

import { TryItResponse } from 'api/documentation';

import { DocumentationCodeBlock } from '../DocumentationCodeBlock';

import { formatResponseBody } from './utils';

// Very large bodies would freeze the page; show the start and say so.
const MAX_SHOWN = 200_000;

/** Status, timing and body of a Try it request. */
export const ApiTryItResponse = ({ response }: { response: TryItResponse }) => {
  const body = useMemo(() => formatResponseBody(response.body), [response]);
  const ok = response.status >= 200 && response.status < 300;
  const truncated = body.length > MAX_SHOWN;
  return (
    <Box mt={4}>
      <Flex align="center" gap={3} mb={2}>
        <Badge colorScheme={ok ? 'green' : 'red'} fontSize="12px">
          {response.status} {response.statusText}
        </Badge>
        <Text fontSize="13px" color="gray.500">
          {response.durationMs} ms ·{' '}
          {(new Blob([response.body]).size / 1024).toFixed(1)} KB
        </Text>
      </Flex>
      {response.status === 401 && (
        <Text fontSize="14px" color="gray.600" mb={2}>
          Your session may have expired. Refresh the page and try again.
        </Text>
      )}
      {body ? (
        <DocumentationCodeBlock
          code={truncated ? body.slice(0, MAX_SHOWN) : body}
          maxH="480px"
        />
      ) : (
        <Text fontSize="14px" color="gray.500">
          The response has no body.
        </Text>
      )}
      {truncated && (
        <Text fontSize="13px" color="gray.500" mt={-3}>
          Showing the first {MAX_SHOWN.toLocaleString()} characters. Use the
          code samples to get the full response.
        </Text>
      )}
    </Box>
  );
};
